import React, { useState } from 'react';
import { X, UserPlus, Mail, IdCard, Loader2, CheckCircle, AlertCircle, Gamepad2, Calendar, ShieldCheck, Globe } from 'lucide-react';
import { createRegistration } from '../lib/supabase';
import { useData } from '../context/DataContext';
import ReactMarkdown from 'react-markdown';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose }) => {
  const { data } = useData();
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Validation
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  
  // Check if user is at least 18 years old
  const isAdult = (dateString: string): boolean => {
    if (!dateString) return false;
    const today = new Date();
    const birth = new Date(dateString);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const isFormValid = 
    userId.trim().length >= 3 && 
    isValidEmail(email) && 
    birthDate !== '' && 
    isAdult(birthDate) &&
    agreedToTerms;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) return;

    setFormState('submitting');
    setErrorMessage('');

    try {
      await createRegistration({
        user_id: userId.trim(),
        email: email.trim(),
        birth_date: birthDate
      });
      
      setFormState('success');
      
      // Reset form after success
      setTimeout(() => {
        setUserId('');
        setEmail('');
        setBirthDate('');
        setAgreedToTerms(false);
        setFormState('idle');
        onClose();
      }, 3000);
      
    } catch (error: any) {
      setFormState('error');
      setErrorMessage(error.message || 'Erro ao processar cadastro. Tente novamente.');
    }
  };

  const handleClose = () => {
    if (formState !== 'submitting') {
      setUserId('');
      setEmail('');
      setBirthDate('');
      setAgreedToTerms(false);
      setFormState('idle');
      setErrorMessage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  // Calculate max date (18 years ago from today)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split('T')[0];

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in" />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md bg-slate-950 border border-white/10 rounded-2xl shadow-2xl animate-slide-up overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header Gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={formState === 'submitting'}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors z-10 disabled:opacity-50"
          aria-label="Fechar"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="relative p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-emerald-500/10 rounded-2xl border border-emerald-500/20">
              <Gamepad2 size={40} className="text-emerald-400" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-white mb-2">
              Entrar no Servidor
            </h2>
            <p className="text-slate-400 text-sm">
              Preencha seus dados para solicitar acesso ao servidor Games no Linux.
            </p>
          </div>

          {/* Form */}
          {formState === 'success' ? (
            <div className="text-center py-8 animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 rounded-full mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Solicitação Enviada!</h3>
              <p className="text-slate-400 text-sm">
                Seu cadastro foi recebido. Nossa equipe irá analisar e liberar seu acesso em breve.
                <br />
                <span className="text-emerald-400">Fique atento ao seu e-mail!</span>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Message */}
              {formState === 'error' && (
                <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl animate-fade-in">
                  <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{errorMessage}</p>
                </div>
              )}
              
              {/* Server IP Info */}
              <div className="flex items-center justify-between p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl mb-2">
                <div className="flex items-center gap-3">
                  <Globe size={18} className="text-emerald-400" />
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Endereço do Servidor</span>
                    <span className="text-sm font-mono text-emerald-100 italic">{data.siteConfig.serverIp}</span>
                  </div>
                </div>
                <div className="px-2 py-1 bg-emerald-500/10 rounded text-[10px] text-emerald-400 font-bold uppercase tracking-widest border border-emerald-500/20">
                  Online
                </div>
              </div>

              {/* User ID Field */}
              <div>
                <label htmlFor="userId" className="block text-sm font-medium text-slate-300 mb-2">
                  <IdCard size={14} className="inline mr-2" />
                  ID do Jogador <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="userId"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Seu nome de usuário ou ID"
                  disabled={formState === 'submitting'}
                  className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-xl text-white placeholder-slate-500 
                           focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 
                           transition-all disabled:opacity-50"
                  minLength={3}
                  maxLength={50}
                  required
                />
                {userId.length > 0 && userId.length < 3 && (
                  <p className="text-amber-400 text-xs mt-1">Mínimo 3 caracteres</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  <Mail size={14} className="inline mr-2" />
                  E-mail <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  disabled={formState === 'submitting'}
                  className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-xl text-white placeholder-slate-500 
                           focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 
                           transition-all disabled:opacity-50"
                  required
                />
                {email.length > 0 && !isValidEmail(email) && (
                  <p className="text-amber-400 text-xs mt-1">E-mail inválido</p>
                )}
              </div>

              {/* Birth Date Field */}
              <div>
                <label htmlFor="birthDate" className="block text-sm font-medium text-slate-300 mb-2">
                  <Calendar size={14} className="inline mr-2" />
                  Data de Nascimento <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  id="birthDate"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  max={maxDateString}
                  disabled={formState === 'submitting'}
                  className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded-xl text-white placeholder-slate-500 
                           focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 
                           transition-all disabled:opacity-50 [color-scheme:dark]"
                  required
                />
                {birthDate && !isAdult(birthDate) && (
                  <p className="text-red-400 text-xs mt-1">Você precisa ter pelo menos 18 anos para se cadastrar.</p>
                )}
              </div>

              {/* Terms Agreement Checkbox */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={formState === 'submitting'}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-slate-900 text-emerald-500 focus:ring-emerald-500/20 focus:ring-offset-0"
                  />
                  <div className="text-sm">
                    <div className="flex items-center gap-2 text-amber-400 font-medium mb-1">
                      <ShieldCheck size={14} />
                      Declaração de Veracidade
                    </div>
                    <div className="text-slate-400 text-xs leading-relaxed prose prose-invert prose-emerald max-w-none">
                      <ReactMarkdown>{data.siteConfig.veracityDeclaration}</ReactMarkdown>
                    </div>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || formState === 'submitting'}
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 
                         text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 
                         transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/30
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
                         flex items-center justify-center gap-2"
              >
                {formState === 'submitting' ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    Solicitar Acesso
                  </>
                )}
              </button>

              {/* Info */}
              <p className="text-center text-slate-500 text-xs mt-4">
                Ao se cadastrar, você concorda com as{' '}
                <a href="#/rules" className="text-emerald-400 hover:underline">regras do servidor</a>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
